import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Select } from '@opendate-app/setlist';
import TemplateSummaryCard from './TemplateSummaryCard';
import NoteChips from './NoteChips';
import useTemplateSummary from '../hooks/useTemplateSummary';
import { budgetTemplateOptions } from '../utils/budgetTemplateOptions';
import { offerSummaryRows, budgetSummaryRows } from '../utils/templateSummary';
import V3OfferTemplateEditDrawer from '../../OfferEditUI/components/V3OfferTemplateEditDrawer';
import V3BudgetTemplateEditDrawer from '../../OfferEditUI/components/V3BudgetTemplateEditDrawer';
import { MANAGED_OFFER_TEMPLATE_NOTICE, MANAGED_BUDGET_TEMPLATE_NOTICE } from '../../OfferEditUI/constants/showManagedTemplateNotices';
import useVenueAgeRestrictionOptions from '../../shared/hooks/useVenueAgeRestrictionOptions';
import withSavedOption from '../../shared/utils/withSavedOption';
import '../styles/eventTemplatesManager.css';

const inputStyle = (invalid) => ({
  background: 'var(--sl-bg-primary)',
  color: 'var(--sl-text-primary)',
  borderRadius: 'var(--radius-md)',
  border: `1px solid ${invalid ? 'var(--sl-border-error)' : 'var(--sl-border-primary)'}`,
});

const EventTemplateEditPanel = ({
  eventTemplateForForm, eventTemplateForFormChanged,
  offerTemplates, budgetTemplates, noteTemplates, noteTemplatesChanged,
  teamId, csrfToken, onCancel, onSave,
}) => {
  const [editingOffer, setEditingOffer] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);

  const offer = useTemplateSummary({ teamId, kind: 'offer', templateId: eventTemplateForForm.v3_offer_template_id, csrfToken });
  const budget = useTemplateSummary({ teamId, kind: 'budget', templateId: eventTemplateForForm.v3_budget_template_id, csrfToken });
  const { options: ageRestrictionOptions } = useVenueAgeRestrictionOptions(teamId, null);

  const offerRows = useMemo(() => offerSummaryRows(offer.data), [offer.data]);
  const offerManagedByShowSaves = offer.data?.managed_by_show_saves ?? false;
  const canEditOfferDealTerms = !!offer.data && !offerManagedByShowSaves;
  const budgetRows = useMemo(() => budgetSummaryRows(budget.data && budget.data.v3_budget_line_items), [budget.data]);
  const budgetManagedByShowSaves = budget.data?.managed_by_show_saves ?? false;
  const canEditBudgetLineItems = !!budget.data && !budgetManagedByShowSaves;

  const setField = (patch) => eventTemplateForFormChanged({ ...eventTemplateForForm, ...patch });

  const handleSave = () => {
    if (!eventTemplateForForm.name) {
      eventTemplateForFormChanged({ ...eventTemplateForForm, errors: ['name'] });
      return;
    }
    onSave(eventTemplateForForm);
  };

  return (
    <div className="p-4 w-100" style={{ background: 'var(--sl-bg-secondary)' }}>
      <div className="d-flex">
        <div className="flex-grow-1">
          <label className="mb-0 text-uppercase" style={{ fontSize: '12px', color: 'var(--sl-text-tertiary)' }}>Name</label>
          <input
            type="text"
            className="form-control"
            style={inputStyle(eventTemplateForForm.errors && eventTemplateForForm.errors.includes('name'))}
            value={eventTemplateForForm.name}
            onChange={(e) => setField({ name: e.target.value, errors: (eventTemplateForForm.errors || []).filter((x) => x !== 'name') })}
          />
        </div>
        <div className="ml-4 flex-grow-1">
          <label className="mb-0 text-uppercase" style={{ fontSize: '12px', color: 'var(--sl-text-tertiary)' }}>Description</label>
          <input
            type="text"
            className="form-control"
            style={inputStyle(false)}
            value={eventTemplateForForm.description || ''}
            onChange={(e) => setField({ description: e.target.value })}
          />
        </div>
        <div className="ml-4">
          <label id="event-template-age-restriction-label" className="mb-0 text-uppercase event-template-field-label">Age Restriction</label>
          <Select
            id="event-template-age-restriction"
            aria-labelledby="event-template-age-restriction-label"
            clearable
            width={220}
            placeholder="None"
            options={withSavedOption(ageRestrictionOptions, eventTemplateForForm.age_restriction)}
            value={eventTemplateForForm.age_restriction || null}
            onChange={(value) => setField({ age_restriction: value || null })}
          />
        </div>
        <div className="ml-4 pt-4 d-flex align-items-start">
          <button type="button" className="team-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="team-btn-primary ml-2" onClick={handleSave}>Save</button>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-6">
          <TemplateSummaryCard
            label="Offer Template"
            options={offerTemplates}
            value={eventTemplateForForm.v3_offer_template_id || ''}
            onChange={(id) => setField({ v3_offer_template_id: id })}
            usedByCount={offer.data ? offer.data.used_by_event_template_count : null}
            summaryRows={offerRows}
            loading={offer.loading}
            error={offer.error}
            onEdit={canEditOfferDealTerms ? () => setEditingOffer(true) : undefined}
            editLabel="Edit deal terms"
            notice={offerManagedByShowSaves ? MANAGED_OFFER_TEMPLATE_NOTICE : null}
          />
        </div>
        <div className="col-6">
          <TemplateSummaryCard
            label="Budget Template"
            options={budgetTemplateOptions(budgetTemplates, eventTemplateForForm)}
            value={eventTemplateForForm.v3_budget_template_id || ''}
            onChange={(id) => setField({ v3_budget_template_id: id })}
            usedByCount={budget.data ? budget.data.used_by_event_template_count : null}
            summaryRows={budgetRows}
            loading={budget.loading}
            error={budget.error}
            onEdit={canEditBudgetLineItems ? () => setEditingBudget(true) : undefined}
            editLabel="Edit line items"
            notice={budgetManagedByShowSaves ? MANAGED_BUDGET_TEMPLATE_NOTICE : null}
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-2 text-uppercase" style={{ fontSize: '12px', color: 'var(--sl-text-tertiary)' }}>Notes</label>
        <NoteChips
          notes={(eventTemplateForForm.notes || []).filter((n) => !n._destroy)}
          noteTemplates={noteTemplates}
          onChange={(nextNotes) => setField({ notes: nextNotes })}
          csrfToken={csrfToken}
          teamId={teamId}
          noteTemplatesChanged={noteTemplatesChanged}
          eventTemplateForForm={eventTemplateForForm}
          eventTemplateForFormChanged={eventTemplateForFormChanged}
        />
      </div>

      <V3OfferTemplateEditDrawer
        team={{ id: teamId }}
        templateId={editingOffer ? eventTemplateForForm.v3_offer_template_id : null}
        open={editingOffer}
        onClose={() => { setEditingOffer(false); offer.reload(); }}
        onSaved={() => { setEditingOffer(false); offer.reload(); }}
        csrfToken={csrfToken}
        contextLabel="Event template › offer template"
      />
      <V3BudgetTemplateEditDrawer
        team={{ id: teamId }}
        templateId={editingBudget ? eventTemplateForForm.v3_budget_template_id : null}
        open={editingBudget}
        onClose={() => { setEditingBudget(false); budget.reload(); }}
        csrfToken={csrfToken}
        currentUser={{}}
      />
    </div>
  );
};

EventTemplateEditPanel.propTypes = {
  eventTemplateForForm: PropTypes.object.isRequired,
  eventTemplateForFormChanged: PropTypes.func.isRequired,
  offerTemplates: PropTypes.array.isRequired,
  budgetTemplates: PropTypes.array.isRequired,
  noteTemplates: PropTypes.array.isRequired,
  noteTemplatesChanged: PropTypes.func.isRequired,
  teamId: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EventTemplateEditPanel;
